/**
 * DevPerformanceChart
 * Vue journalière : barres verticales empilées SP complétés par jour et par développeur
 */

import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from 'recharts';
import { Users2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

// Palette de couleurs par développeur (vue journalière)
const DEV_COLORS = [
  '#6B4C9A', '#10B981', '#3B82F6', '#F59E0B',
  '#EF4444', '#EC4899', '#14B8A6', '#F97316',
  '#84CC16', '#8B5CF6', '#06B6D4', '#D97706'
];

// ── Tooltip vue journalière ──
const DailyTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  const entries = payload.filter(p => p.value > 0).reverse();
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 px-4 py-3 min-w-[190px]">
      <p className="text-xs font-semibold text-gray-500 mb-2">
        {label ? format(parseISO(label), 'd MMMM yyyy', { locale: fr }) : ''}
      </p>
      <div className="space-y-1">
        {entries.map(p => (
          <div key={p.dataKey} className="flex justify-between gap-4 text-xs">
            <span className="font-medium truncate" style={{ color: p.fill }}>{p.dataKey}</span>
            <span className="font-bold shrink-0">{p.value} SP</span>
          </div>
        ))}
      </div>
      {entries.length > 1 && (
        <div className="flex justify-between gap-4 text-xs pt-1.5 mt-1 border-t border-gray-100">
          <span className="text-gray-500 font-medium">Total</span>
          <span className="font-bold">{total} SP</span>
        </div>
      )}
    </div>
  );
};

// ── Vue journalière ──
const DailyView = ({ dailyData, devNames }) => {
  if (dailyData.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-sm text-gray-400">
        Aucune tâche complétée avec des SP enregistrés
      </div>
    );
  }

  const formatDate = (d) => {
    try { return format(parseISO(d), 'dd/MM', { locale: fr }); } catch { return d; }
  };

  return (
    <div style={{ height: 320 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={dailyData}
          margin={{ top: 10, right: 16, left: -10, bottom: 4 }}
          barCategoryGap="20%"
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={formatDate}
            tick={{ fontSize: 10, fill: '#9CA3AF' }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#9CA3AF' }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <Tooltip content={<DailyTooltip />} cursor={{ fill: '#F9FAFB' }} />

          {devNames.map((name, i) => (
            <Bar
              key={name}
              dataKey={name}
              stackId="a"
              fill={DEV_COLORS[i % DEV_COLORS.length]}
              radius={
                i === devNames.length - 1
                  ? [3, 3, 0, 0]
                  : i === 0
                    ? [0, 0, 3, 3]
                    : 0
              }
              name={name}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ── Composant principal ──
const DevPerformanceChart = ({
  devPerformance = [],
  devNames = [],
  dailyData = [],
  sprintActif = false
}) => {
  const [showAll, setShowAll] = useState(false);

  const activeDevs    = devPerformance.filter(d => d.isActive !== false);
  const displayed     = showAll ? devPerformance : activeDevs;
  const archivedCount = devPerformance.length - activeDevs.length;

  // Noms cohérents avec la liste affichée (vue journalière)
  const displayedNames = devNames.filter(n => displayed.some(d => d.name === n));

  const isEmpty = displayed.length === 0;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center shrink-0">
            <Users2 className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">
              Performances développeurs{sprintActif ? ' — Sprint actif' : ''}
            </h3>
            <p className="text-xs text-gray-500">
              {isEmpty
                ? 'Aucune tâche assignée'
                : `${displayed.length} développeur${displayed.length > 1 ? 's' : ''} · Story Points`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Légende vue journalière */}
          {displayedNames.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              {displayedNames.map((name, i) => (
                <div key={name} className="flex items-center gap-1.5">
                  <div
                    className="w-3 h-3 rounded-sm shrink-0"
                    style={{ backgroundColor: DEV_COLORS[i % DEV_COLORS.length] }}
                  />
                  <span className="text-xs text-gray-500 truncate max-w-[80px]">
                    {name.split(' ')[0]}
                  </span>
                </div>
              ))}
            </div>
          )}
          {archivedCount > 0 && (
            <button
              onClick={() => setShowAll(v => !v)}
              className={`text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors ${
                showAll
                  ? 'bg-amber-500 text-white border-amber-500'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-amber-400 hover:text-amber-600'
              }`}
            >
              {showAll ? `Actifs seulement` : `+ ${archivedCount} archivé${archivedCount > 1 ? 's' : ''}`}
            </button>
          )}
        </div>
      </div>

      {isEmpty ? (
        <div className="flex items-center justify-center h-24 text-sm text-gray-400">
          Aucune tâche assignée trouvée
        </div>
      ) : (
        <DailyView dailyData={dailyData} devNames={displayedNames} />
      )}
    </div>
  );
};

export default DevPerformanceChart;
